import { useQuery } from "@tanstack/react-query";
import { isAfter } from "date-fns";
import { CreditCard } from "lucide-react";
import { Portal } from "radix-ui";
import { useTRPC } from "@/lib/trpc";
import BillingSettings from "./settings/SubscriptionSettings";

export function TrialExpiredDialog() {
    const trpc = useTRPC();
    // We don't use the default useSubscription hook because we want to refetch the subscription data every 3 minutes.
    const { data: subscriptionData, isLoading } = useQuery(
        trpc.subscription.get.queryOptions(
            {},
            {
                staleTime: 10 * 1000, // 10 seconds. We ask this thing a couple of times in a row when loading, so we cache it for a couple of seconds to avoid refetching.
                gcTime: 10 * 1000, // 10 seconds
                refetchOnWindowFocus: true,
                refetchInterval: 3 * 60 * 1000, // every 3 minutes
            }
        )
    );

    if (isLoading) {
        return null;
    }

    const hasSubscription = !!subscriptionData?.subscription;
    if (hasSubscription) {
        return null;
    }

    // Trial still active?
    if (subscriptionData?.trialEndsAt && isAfter(subscriptionData.trialEndsAt, new Date())) {
        return null;
    }

    return (
        <Portal.Root>
            <div
                data-state="open"
                data-slot="dialog-overlay"
                className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/10 backdrop-blur-sm"
                style={{ pointerEvents: "auto" }}
                data-aria-hidden="true"
                aria-hidden="true"
            ></div>
            <div
                role="dialog"
                className="bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full translate-x-[-50%] translate-y-[-50%] rounded-md border shadow-lg duration-200 max-w-md sm:max-w-2xl"
                tabIndex={-1}
                style={{ pointerEvents: "auto" }}
            >
                <div data-slot="dialog-header" className="flex flex-col gap-2 text-center sm:text-left p-6 border-b">
                    <h2 data-slot="dialog-title" className="text-lg leading-none font-semibold flex items-center gap-2">
                        <CreditCard className="size-4" />
                        Billing
                    </h2>
                </div>
                <div className="p-6">
                    <BillingSettings />
                </div>
            </div>
        </Portal.Root>
    );
}
